import { ChangeEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import InaccessibleImeiDialog from "@/components/PageHeader/actions/dialogs/vehicles/InaccessibleImeiDialog/InaccessibleImeiDialog";
import { useVehiclesTableStore } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { VehicleDataDto } from "../../../../../../Pages/Vehicles/interfaces";
import {
  GeneralDialogProps,
  INPUT_LENGTH,
  ReassignVehicleData,
  ROUTES,
  useMediaQueries,
  VEHICLES_ACTIONS
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import {
  capitalizeResourceFirstLetter,
  refreshTableAndUpdateDetails
} from "../../../../../../shared/functions/functions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import useVehiclesFormInputs from "../../../../../../shared/hooks/useVehiclesFormInputs";
import useTableItemDetails from "../../../../../DetailsSection/hooks/useTableItemDetails";
import DialogTitleWithTooltip from "../../../../../Dialog/components/DialogTitleWithTooltip/DialogTitleWithTooltip";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import EditVehicleDialogContent from "./EditVehicleDialogContent";
import ExchangeImeiDialog from "./ExchangeImeiDialog/ExchangeImeiDialog";

interface IEditVehicleDialogProps {
  rowData: VehicleDataDto | null;
}

const EditVehicleDialog = ({
  rowData,
  isOpen,
  onClose
}: IEditVehicleDialogProps & GeneralDialogProps) => {
  const { toMd } = useMediaQueries();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleResponse, handleVehicleActionError } = useResponse();
  const { t } = useTranslation();

  const {
    detailsData,
    params: { contains },
    autoAssignVrnEnabled
  } = useVehiclesTableStore();

  const {
    descriptionInputError,
    descriptionValue,
    imeiError,
    imeiIsValid,
    imeiValue,
    isVehicleDeactivated,
    nameInputError,
    vehicleName,
    shouldVehicleNameValidate,
    handleChangeDescription,
    handleChangeImeiValue,
    handleChangeNameValue,
    handleToggleDeactivateVehicle,
    validateDescriptionLength,
    validateImei,
    validateNameLength,
    setImeiError,
    setNameInputError,
    handleCustomError
  } = useVehiclesFormInputs(rowData ?? undefined);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [reassignData, setReassignData] = useState<ReassignVehicleData | null>(
    null
  );
  const [overwriteImei, setOverwriteImei] = useState(false);

  const payload = {
    id: rowData?.id ?? null,
    imei: imeiValue === "" ? null : imeiValue,
    name: vehicleName,
    comment: descriptionValue,
    disabled: isVehicleDeactivated,
    forceReassignImei: false,
    confirmImeiConflictResolution: false
  };

  const [inaccessibleImeiOpen, setInaccessibleImeiOpen] = useState(false);

  const descriptionIsValid =
    (descriptionValue && descriptionValue.length <= INPUT_LENGTH.Description) ||
    !descriptionValue;

  const isReadyToConfirm =
    shouldVehicleNameValidate && descriptionIsValid && imeiIsValid;

  const handleCloseEditVehiclesDialog = () => {
    onClose(VEHICLES_ACTIONS.EditVehicle);
  };

  const handleSubmit = async (confirmConflict?: boolean) => {
    const resource = ROUTES.Vehicles;

    if (isReadyToConfirm) {
      try {
        setIsLoading(true);

        const finalPayload = {
          ...payload,
          forceReassignImei: overwriteImei,
          confirmImeiConflictResolution: Boolean(confirmConflict)
        };

        await putData(resource, finalPayload);

        const resourceString = capitalizeResourceFirstLetter(resource).slice(
          0,
          -1
        );

        const editableItemHasOpenedDetails =
          detailsData && rowData && detailsData.id === rowData.id;

        refreshTableAndUpdateDetails({
          valueInSearchField: contains,
          resourceId: payload.id,
          successMessage: t(`Alert##successfully edited`, { resourceString }),
          itemHasOpenedDetails: editableItemHasOpenedDetails,
          getTableItemDetails,
          handleResponse
        });

        handleCloseEditVehiclesDialog();
      } catch (error) {
        handleVehicleActionError(
          error,
          setIsLoading,
          setImeiError,
          setNameInputError,
          t,
          () =>
            handleCustomError(
              error,
              setReassignData,
              setIsExchangeOpen,
              setInaccessibleImeiOpen
            )
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleExchangeClose = (withSubmit?: boolean) => {
    setIsExchangeOpen(false);
    if (withSubmit) {
      handleSubmit(true);
    }
  };

  const handleOverwriteCheckbox = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setOverwriteImei(checked);
  };

  const resetAllValues = () => {
    setReassignData(null);
  };

  return (
    <>
      <Dialog
        data-testid="edit-vehicle-dialog"
        title={
          rowData &&
          ((
            <DialogTitleWithTooltip
              title={t("Dialog##edit vehicle", { name: rowData.number })}
              tooltipText={rowData.number}
            />
          ) as any)
        }
        extendToMaxWidth={toMd}
        isTitleSeparator
        isActionsSeparator
        open={isOpen}
        close={handleCloseEditVehiclesDialog}
        submit={handleSubmit}
        width={toMd ? undefined : DialogWidth.MinWidth}
        slotProps={{ transition: { onExited: () => resetAllValues() } }}
        actions={
          <DialogActions
            onClose={handleCloseEditVehiclesDialog}
            isLoading={isLoading}
            isreadyToConfirm={isReadyToConfirm}
            confirmationButtonText={t("Button##save")}
            confirmationButtonTestId="edit-vehicle-button"
            cancelButtonText={t("Button##close")}
            cancelButtonTestId="close-edit-vehicle"
          />
        }
      >
        {rowData && (
          <EditVehicleDialogContent
            payload={payload}
            nameErrorText={nameInputError}
            descriptionErrorText={
              descriptionIsValid ? "" : descriptionInputError
            }
            isLoading={isLoading}
            onNameChange={handleChangeNameValue}
            onImeiValueChange={handleChangeImeiValue}
            onDescriptionChange={handleChangeDescription}
            toggleVehicleDeactivate={handleToggleDeactivateVehicle}
            validateDescription={validateDescriptionLength}
            validateImei={validateImei}
            validateName={validateNameLength}
            imeiErrorText={imeiError}
            autoAssignVrnEnabled={autoAssignVrnEnabled}
          />
        )}
      </Dialog>

      <ExchangeImeiDialog
        isOpen={isExchangeOpen}
        dialogData={reassignData}
        isChecked={overwriteImei}
        handleCheckbox={handleOverwriteCheckbox}
        onClose={() => handleExchangeClose(false)}
        onSubmit={() => handleExchangeClose(true)}
      />

      <InaccessibleImeiDialog
        isOpen={inaccessibleImeiOpen}
        onClose={() => setInaccessibleImeiOpen(false)}
      />
    </>
  );
};

export default EditVehicleDialog;
